import { useEffect, useMemo, useRef, useState } from 'react';
import { getPublicModelsAPI, downloadTrainedModelAPI, incrementDownloadAPI } from '../api/modelHubApi';
import useModelHubStore from '../store/useModelHubStore';
import { ownerKey } from '../hubMeta';
import { useSwal } from '../../../../components/Notifications/swal/SwalCom';

/**
 * Owns all Public Model Hub data + interactions.
 * Fetching happens once; search / type / owner filtering and sorting are all client-side
 * for instant feedback (the public model set is small).
 */
export const useModelHub = () => {
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState(null);

  const { searchTerm, activeTypes, sortBy, ownerFilter, setOwnerFilter } = useModelHubStore();
  const { swalSuccess, swalFail } = useSwal();
  const swalFailRef = useRef(swalFail);
  swalFailRef.current = swalFail;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getPublicModelsAPI()
      .then((res) => {
        if (cancelled) return;
        const data = res?.data;
        setModels(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Failed to fetch public models:', err);
        setModels([]);
        swalFailRef.current?.('Error', 'Could not load public models. Please try again.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Owners derived from the loaded set (powers the owner dropdown). Keyed by
  // `ownerKey` — the same function the filter below uses, so an entry in the list
  // can never fail to match the models it was counted from.
  const owners = useMemo(() => {
    const map = new Map();
    models.forEach((m) => {
      const id = ownerKey(m);
      const entry = map.get(id);
      if (entry) {
        entry.count += 1;
        return;
      }
      map.set(id, { id, name: String(m.owner_name || '').trim() || 'Unknown', count: 1 });
    });

    const list = Array.from(map.values());

    // Two owners whose user doc is missing both read "Unknown" — indistinguishable
    // in the dropdown, so suffix the colliding ones with a short id.
    const nameUses = new Map();
    list.forEach((o) => nameUses.set(o.name, (nameUses.get(o.name) || 0) + 1));
    list.forEach((o) => {
      if (nameUses.get(o.name) > 1) o.name = `${o.name} (${o.id.slice(-6)})`;
    });

    // Busiest owners first — that is the useful order in a directory — then A-Z.
    return list.sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
  }, [models]);

  // The store outlives this page, so a filter set on an earlier visit can point at
  // an owner who is not in the fetched set any more. Left alone that renders an
  // empty hub with no explanation, so drop it once the real owner list is known.
  useEffect(() => {
    if (loading || !ownerFilter || !models.length) return;
    if (owners.some((o) => o.id === ownerFilter)) return;
    setOwnerFilter('');
  }, [loading, ownerFilter, models.length, owners, setOwnerFilter]);

  // Client-side filter + sort pipeline.
  const filteredModels = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    let list = models.filter((m) => {
      if (activeTypes.length && !activeTypes.includes(m.model_type)) return false;
      if (ownerFilter && ownerKey(m) !== ownerFilter) return false;
      if (term) {
        const haystack = `${m.model_name || ''} ${m.model_detail || ''} ${(m.tags || []).join(' ')}`.toLowerCase();
        if (!haystack.includes(term)) return false;
      }
      return true;
    });

    const byTime = (m) => new Date(m.created_on || 0).getTime() || 0;
    list = [...list].sort((a, b) => {
      switch (sortBy) {
        case 'oldest':
          return byTime(a) - byTime(b);
        case 'name':
          return (a.model_name || '').localeCompare(b.model_name || '');
        case 'downloads':
          return (b.download_count || 0) - (a.download_count || 0);
        case 'versions':
          return (b.version_count || 0) - (a.version_count || 0);
        case 'newest':
        default:
          return byTime(b) - byTime(a);
      }
    });
    return list;
  }, [models, searchTerm, activeTypes, ownerFilter, sortBy]);

  const handleDownload = async (model, version) => {
    const modelType = model?.model_type || 'classification';
    const modelDataInfoId = version?._id;
    if (!modelDataInfoId) return;

    setDownloadingId(modelDataInfoId);
    try {
      const response = await downloadTrainedModelAPI({ modelType, modelDataInfoId });
      const contentDisposition = response.headers?.['content-disposition'];
      let filename = `training_output_${modelDataInfoId}_${modelType}.zip`;
      if (contentDisposition?.includes('filename=')) {
        filename = contentDisposition.split('filename=')[1].replace(/"/g, '');
      }
      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      // Optimistically bump the local counter, then persist.
      setModels((prev) =>
        prev.map((m) =>
          m.model_id === model.model_id
            ? { ...m, download_count: (m.download_count || 0) + 1 }
            : m,
        ),
      );
      incrementDownloadAPI(model.model_id).catch((e) =>
        console.warn('Could not persist download count:', e),
      );

      swalSuccess('Download started', `${model?.model_name} — version downloaded.`);
    } catch (err) {
      console.error('Download failed:', err);
      swalFail('Download failed', err?.response?.data?.detail || err?.message || 'Could not download model.');
    } finally {
      setDownloadingId(null);
    }
  };

  return {
    models,
    filteredModels,
    owners,
    loading,
    downloadingId,
    totalCount: models.length,
    handleDownload,
  };
};

export default useModelHub;
